"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { Loader2, Dog, Cat } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchBreeds } from "@/api/simpego";
import PetNameInput from "./pet-details/PetNameInput";
import GenderSelection from "./pet-details/GenderSelection";
import BreedTypeSelection from "./pet-details/BreedTypeSelection";
import BreedAndSizeSelection from "./pet-details/BreedAndSizeSelection";
import CastrationSelection from "./pet-details/CastrationSelection";
import HouseCatSelection from "./pet-details/HouseCatSelection";
import BirthDateInput from "./pet-details/BirthDateInput";
import PreexistingConditionSelection from "./pet-details/PreexistingConditionSelection";
import ChipNumberInput from "./pet-details/ChipNumberInput";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface PetDetailsProps {
  petType: "cat" | "dog" | null;
  onDetailsSubmit: (
    petName: string,
    breed: string | null,
    breedLabel: string | null,
    birthDate: Date | undefined,
    selectedBreedType: "purebred" | "crossbreed" | "mixed",
    gender: "male" | "female" | "unknown",
    isCastrated: boolean,
    isHouseCat: boolean,
    selectedDogSize: "small" | "large" | null,
    preexistingCoverage: boolean,
    chipNumber: string | null
  ) => void;
  onBack: () => void;
  initialPetName: string | null;
  initialBreed: string | null;
  initialBirthDate: Date | undefined;
  initialSelectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  initialGender: "male" | "female" | "unknown" | null;
  initialIsCastrated: boolean | null;
  initialIsHouseCat: boolean | null;
  initialSelectedDogSize: "small" | "large" | null;
  initialPreexistingCoverage: boolean | null;
  initialChipNumber: string | null;
  onPetTypeChange: (petType: "cat" | "dog") => void;
}

const PetDetails: React.FC<PetDetailsProps> = ({
  petType,
  onDetailsSubmit,
  onBack,
  initialPetName,
  initialBreed,
  initialBirthDate,
  initialSelectedBreedType,
  initialGender,
  initialIsCastrated,
  initialIsHouseCat,
  initialSelectedDogSize,
  initialPreexistingCoverage,
  initialChipNumber,
  onPetTypeChange,
}) => {
  const [petName, setPetName] = React.useState<string>(initialPetName || "");
  const [selectedBreed, setSelectedBreed] = React.useState<string>(initialBreed || "");
  const [birthDate, setBirthDate] = React.useState<Date | undefined>(initialBirthDate);
  const [selectedBreedType, setSelectedBreedType] = React.useState<"purebred" | "crossbreed" | "mixed" | null>(initialSelectedBreedType);
  const [selectedGender, setSelectedGender] = React.useState<"male" | "female" | null>(
    initialGender === "male" || initialGender === "female" ? initialGender : null
  );
  const [isCastrated, setIsCastrated] = React.useState<boolean | null>(petType ? initialIsCastrated : null);
  const [isHouseCat, setIsHouseCat] = React.useState<boolean | null>(petType ? initialIsHouseCat : null);
  const [selectedDogSize, setSelectedDogSize] = React.useState<"small" | "large" | null>(initialSelectedDogSize);
  const [preexistingCoverage, setPreexistingCoverage] = React.useState<boolean | null>(petType ? initialPreexistingCoverage : null);
  const [chipNumber, setChipNumber] = React.useState<string>(initialChipNumber || "");
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const petNameInputRef = React.useRef<HTMLInputElement>(null);
  const genderGroupRef = React.useRef<HTMLDivElement>(null);
  const breedTypeGroupRef = React.useRef<HTMLDivElement>(null);
  const breedComboboxRef = React.useRef<HTMLDivElement>(null);
  const dogSizeGroupRef = React.useRef<HTMLDivElement>(null);
  const castrationGroupRef = React.useRef<HTMLDivElement>(null);
  const houseCatGroupRef = React.useRef<HTMLDivElement>(null);
  const datePickerGroupRef = React.useRef<HTMLDivElement>(null);
  const preexistingCoverageGroupRef = React.useRef<HTMLDivElement>(null);
  const chipNumberInputRef = React.useRef<HTMLInputElement>(null);

  const [errors, setErrors] = React.useState({
    petName: "",
    breed: "",
    birthDate: "",
    gender: "",
    breedType: "",
    castrated: "",
    houseCat: "",
    dogSize: "",
    preexistingCoverage: "",
    chipNumber: "",
  });

  const [localSelectedPetType, setLocalSelectedPetType] = React.useState<"cat" | "dog" | null>(petType);

  const { t, i18n } = useTranslation();

  const catSpeciesIconKey = t("petDetails.catSpeciesIcon", { defaultValue: "" });
  const dogSpeciesIconKey = t("petDetails.dogSpeciesIcon", { defaultValue: "" });

  const widgetBasePath = (window as any).__env__?.widgetBasePath || '';

  const finalCatSpeciesIconSrc = (catSpeciesIconKey && catSpeciesIconKey !== "petDetails.catSpeciesIcon")
    ? `${widgetBasePath}${catSpeciesIconKey}`
    : '';
  const finalDogSpeciesIconSrc = (dogSpeciesIconKey && dogSpeciesIconKey !== "petDetails.dogSpeciesIcon")
    ? `${widgetBasePath}${dogSpeciesIconKey}`
    : '';

  const hasCatSpeciesIcon = !!finalCatSpeciesIconSrc;
  const hasDogSpeciesIcon = !!finalDogSpeciesIconSrc;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: breedsData, isLoading: isLoadingBreeds, isError: isErrorBreeds } = useQuery({
    queryKey: ["breeds", localSelectedPetType, i18n.language],
    queryFn: () => {
      if (!localSelectedPetType) return Promise.resolve([]);
      return fetchBreeds(localSelectedPetType, i18n.language);
    },
    enabled: !!localSelectedPetType,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const breedOptions = React.useMemo(() => {
    if (isLoadingBreeds || isErrorBreeds || !breedsData) {
      return [];
    }
    return (breedsData || []).map((breed) => ({
      value: breed.code,
      label: breed.translation,
    }));
  }, [breedsData, isLoadingBreeds, isErrorBreeds]);

  const findBreedLabel = (code: string | null): string | null => {
    if (!code) return null;
    const found = (breedsData || []).find((b) => b.code === code);
    return found?.translation || null;
  };

  const handleSubmit = async () => {
    let currentErrors = {
      petName: "",
      breed: "",
      birthDate: "",
      gender: "",
      breedType: "",
      castrated: "",
      houseCat: "",
      dogSize: "",
      preexistingCoverage: "",
      chipNumber: "",
    };
    let hasError = false;
    let calculatedAge: number | null = null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!petName.trim()) {
      currentErrors.petName = t("petDetails.validation.enterPetName");
      hasError = true;
    }
    if (selectedGender === null) {
      currentErrors.gender = t("petDetails.validation.selectGender");
      hasError = true;
    }
    if (!selectedBreedType) {
      currentErrors.breedType = t("petDetails.validation.selectBreedType");
      hasError = true;
    } else {
      if ((selectedBreedType === "purebred" || selectedBreedType === "crossbreed") && !selectedBreed) {
        currentErrors.breed = t("petDetails.validation.selectBreed");
        hasError = true;
      }
      if (selectedBreedType === "mixed" && localSelectedPetType === "dog" && selectedDogSize === null) {
        currentErrors.dogSize = t("petDetails.validation.selectDogSize");
        hasError = true;
      }
    }

    if (isCastrated === null) {
      currentErrors.castrated = t("petDetails.validation.selectCastration");
      hasError = true;
    }
    if (localSelectedPetType === "cat" && isHouseCat === null) {
      currentErrors.houseCat = t("petDetails.validation.selectHouseCat");
      hasError = true;
    }
    if (preexistingCoverage === null) {
      currentErrors.preexistingCoverage = t("petDetails.validation.selectPreexistingCoverage");
      hasError = true;
    }
    if (chipNumber && !/^\d{15}$/.test(chipNumber)) {
      currentErrors.chipNumber = t("petDetails.validation.chipNumber");
      hasError = true;
    }

    if (!birthDate) {
      currentErrors.birthDate = t("petDetails.validation.selectBirthDate");
      hasError = true;
    } else if (birthDate > today) {
      currentErrors.birthDate = t("petDetails.validation.futureBirthDate");
      hasError = true;
    } else {
      let ageDiff = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        ageDiff--;
      }
      calculatedAge = ageDiff;
      if (calculatedAge < 0 || calculatedAge > 25) {
        currentErrors.birthDate = t("petDetails.validation.validAge");
        hasError = true;
      }
    }

    setErrors(currentErrors);
    if (hasError) {
      const errorRefs = [
        { ref: petNameInputRef, error: currentErrors.petName },
        { ref: genderGroupRef, error: currentErrors.gender },
        { ref: breedTypeGroupRef, error: currentErrors.breedType },
        { ref: breedComboboxRef, error: currentErrors.breed },
        { ref: dogSizeGroupRef, error: currentErrors.dogSize },
        { ref: castrationGroupRef, error: currentErrors.castrated },
        { ref: houseCatGroupRef, error: currentErrors.houseCat },
        { ref: preexistingCoverageGroupRef, error: currentErrors.preexistingCoverage },
        { ref: datePickerGroupRef, error: currentErrors.birthDate },
        { ref: chipNumberInputRef, error: currentErrors.chipNumber },
      ];

      for (const { ref, error } of errorRefs) {
        const isBreedComboboxRelevant = (selectedBreedType === "purebred" || selectedBreedType === "crossbreed") && ref === breedComboboxRef;
        const isDogSizeRelevant = localSelectedPetType === "dog" && selectedBreedType === "mixed" && ref === dogSizeGroupRef;
        const isHouseCatRelevant = localSelectedPetType === "cat" && ref === houseCatGroupRef;
        const isDatePickerRelevant = ref === datePickerGroupRef && currentErrors.birthDate;
        const isPreexistingCoverageRelevant = ref === preexistingCoverageGroupRef && currentErrors.preexistingCoverage;
        const isChipNumberRelevant = ref === chipNumberInputRef && currentErrors.chipNumber;
        
        if (error && ref.current && (
            (ref !== breedComboboxRef && ref !== dogSizeGroupRef && ref !== houseCatGroupRef && ref !== datePickerGroupRef && ref !== preexistingCoverageGroupRef) ||
            (isBreedComboboxRelevant) ||
            (isDogSizeRelevant) ||
            (isHouseCatRelevant) ||
            (isDatePickerRelevant) ||
            (isPreexistingCoverageRelevant) ||
            (isChipNumberRelevant)
        )) {
          ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      }
      return;
    }

    setIsSubmitting(true);
    try {
      let finalBreed: string | null = null;
      if (selectedBreedType === "purebred" || selectedBreedType === "crossbreed") {
        finalBreed = selectedBreed;
      } else if (selectedBreedType === "mixed") {
        if (localSelectedPetType === "cat") {
          finalBreed = "mischlingskatze";
        } else if (localSelectedPetType === "dog") {
          finalBreed = selectedDogSize === "small" ? "mischling_klein" : "mischling_gross";
        }
      }
      const finalBreedLabel = findBreedLabel(finalBreed);

      await onDetailsSubmit(
        petName.trim(),
        finalBreed,
        finalBreedLabel,
        birthDate,
        selectedBreedType as "purebred" | "crossbreed" | "mixed",
        selectedGender as "male" | "female" | "unknown",
        isCastrated as boolean,
        (isHouseCat as boolean) ?? false,
        selectedDogSize,
        preexistingCoverage as boolean,
        chipNumber.trim() ? chipNumber.trim() : null
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isOverallLoading = isSubmitting || isLoadingBreeds;

  return (
    <Card className="w-full max-w-4xl mx-auto border-none shadow-none">
      <CardHeader>
        <CardTitle className="text-2xl font-bold">
          {t("banner-header-1")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <PetNameInput
          petName={petName}
          setPetName={(name) => {
            setPetName(name);
            setErrors(prev => ({ ...prev, petName: "" }));
          }}
          error={errors.petName}
          isLoading={isOverallLoading}
          inputRef={petNameInputRef}
        />

        <div className="space-y-2">
          <Label>
            {petName
              ? t("petDetails.speciesQuestion", { petName })
              : t("petDetails.speciesQuestionUndefined")}
          </Label>
          <div className="grid grid-cols-2 gap-4 w-full">
            <div
              className={cn(
                "flex flex-col items-center justify-center p-6 border-2 rounded-lg cursor-pointer transition-colors",
                localSelectedPetType === "dog"
                  ? "border-primary ring-2 ring-primary bg-primary/10"
                  : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
              )}
              onClick={() => {
                setLocalSelectedPetType("dog");
                onPetTypeChange("dog");
                setSelectedBreed("");
                setSelectedBreedType(null);
                setSelectedDogSize(null);
                setIsHouseCat(null);
              }}
            >
              {hasDogSpeciesIcon ? (
                <img src={finalDogSpeciesIconSrc} alt={t("petSelection.dog")} className="h-12 w-12 mb-2 object-contain" data-species-icon />
              ) : (
                <Dog className="h-12 w-12 mb-2 text-primary" data-species-icon />
              )}
              <Label className="text-lg font-medium cursor-pointer">{t("petSelection.dog")}</Label>
            </div>
            <div
              className={cn(
                "flex flex-col items-center justify-center p-6 border-2 rounded-lg cursor-pointer transition-colors",
                localSelectedPetType === "cat"
                  ? "border-primary ring-2 ring-primary bg-primary/10"
                  : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover-border-gray-600"
              )}
              onClick={() => {
                setLocalSelectedPetType("cat");
                onPetTypeChange("cat");
                setSelectedBreed("");
                setSelectedBreedType(null);
                setSelectedDogSize(null);
                setIsHouseCat(null);
              }}
            >
              {hasCatSpeciesIcon ? (
                <img src={finalCatSpeciesIconSrc} alt={t("petSelection.cat")} className="h-12 w-12 mb-2 object-contain" data-species-icon />
              ) : (
                <Cat className="h-12 w-12 mb-2 text-primary" data-species-icon />
              )}
              <Label className="text-lg font-medium cursor-pointer">{t("petSelection.cat")}</Label>
            </div>
          </div>
        </div>

        {localSelectedPetType && (
          <>
            <GenderSelection
              petName={petName}
              selectedGender={selectedGender}
              setSelectedGender={(gender) => {
                setSelectedGender(gender);
                setErrors(prev => ({ ...prev, gender: "" }));
              }}
              error={errors.gender}
              isLoading={isOverallLoading}
              groupRef={genderGroupRef}
            />

            <BreedTypeSelection
              petName={petName}
              selectedBreedType={selectedBreedType}
              setSelectedBreedType={(type) => {
                setSelectedBreedType(type);
                setErrors(prev => ({ ...prev, breedType: "", breed: "", dogSize: "" }));
              }}
              setSelectedBreed={(breed) => {
                setSelectedBreed(breed);
                setErrors(prev => ({ ...prev, breed: "" }));
              }}
              setSelectedDogSize={(size) => {
                setSelectedDogSize(size);
                setErrors(prev => ({ ...prev, dogSize: "" }));
              }}
              error={errors.breedType}
              isLoading={isOverallLoading}
              groupRef={breedTypeGroupRef}
            />

            <BreedAndSizeSelection
              petType={localSelectedPetType}
              selectedBreedType={selectedBreedType}
              selectedBreed={selectedBreed}
              setSelectedBreed={(breed) => {
                setSelectedBreed(breed);
                setErrors(prev => ({ ...prev, breed: "" }));
              }}
              selectedDogSize={selectedDogSize}
              setSelectedDogSize={(size) => {
                setSelectedDogSize(size);
                setErrors(prev => ({ ...prev, dogSize: "" }));
              }}
              breedOptions={breedOptions}
              breedError={errors.breed}
              dogSizeError={errors.dogSize}
              isLoading={isOverallLoading}
              breedComboboxRef={breedComboboxRef}
              dogSizeGroupRef={dogSizeGroupRef}
              petName={petName}
            />

            <CastrationSelection
              isCastrated={isCastrated}
              setIsCastrated={(value) => {
                setIsCastrated(value);
                setErrors(prev => ({ ...prev, castrated: "" }));
              }}
              error={errors.castrated}
              isLoading={isOverallLoading}
              groupRef={castrationGroupRef}
              petName={petName}
            />

            {localSelectedPetType === "cat" && (
              <HouseCatSelection
                isHouseCat={isHouseCat}
                setIsHouseCat={(value) => {
                  setIsHouseCat(value);
                  setErrors(prev => ({ ...prev, houseCat: "" }));
                }}
                error={errors.houseCat}
                isLoading={isOverallLoading}
                groupRef={houseCatGroupRef}
                petName={petName}
                petType={localSelectedPetType}
              />
            )}

            <BirthDateInput
              birthDate={birthDate}
              setBirthDate={(date) => {
                setBirthDate(date);
                setErrors(prev => ({ ...prev, birthDate: "" }));
              }}
              error={errors.birthDate}
              isLoading={isSubmitting}
              groupRef={datePickerGroupRef}
              petName={petName}
              petType={localSelectedPetType}
            />

            <PreexistingConditionSelection
              preexistingCoverage={preexistingCoverage}
              setPreexistingCoverage={(value) => {
                setPreexistingCoverage(value);
                setErrors(prev => ({ ...prev, preexistingCoverage: "" }));
              }}
              error={errors.preexistingCoverage}
              isLoading={isOverallLoading}
              groupRef={preexistingCoverageGroupRef}
              petName={petName}
              petType={localSelectedPetType}
            />

            <ChipNumberInput
              chipNumber={chipNumber}
              onChipNumberChange={(value) => {
                setChipNumber(value);
                setErrors(prev => ({ ...prev, chipNumber: "" }));
              }}
              error={errors.chipNumber}
              isLoading={isOverallLoading}
              inputRef={chipNumberInputRef}
            />
          </>
        )}

        <div className="w-full">
          <Button className="w-full bg-continue-button text-continue-button-foreground hover:bg-continue-button/90" onClick={handleSubmit} disabled={isOverallLoading || !localSelectedPetType}>
            {isOverallLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("petDetails.getPrice")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PetDetails;
